import {
  Injectable,
  ComponentRef,
  ViewContainerRef,
  ApplicationRef,
  Injector,
  EmbeddedViewRef,
  ElementRef
} from '@angular/core';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { GetMessagesService } from '../../shared/get-messages.service';
import { PlessDialogService } from '../../shared/pless-dialog-service';
import { PlessAlert } from '../../shared/pless-alert/pless-alert.component';
import { PlessAlertSubComponent } from './pless-alert/pless-alert-sub.component';

/**
 * ダイアログ表示を提供するサービス
 *
 * @export
 * @class PlessDialogServiceSub
 */
@Injectable()
export class PlessDialogServiceSub extends PlessDialogService {
  /** 対象Elementに付与するCLASS名(zIndexの探索に使用) */
  private CLASS_NAME = 'pless-title-window';

  /**
   *Creates an instance of PlessDialogServiceSub.
   * @param {NgbModal} modalServiceSub
   * @memberof PlessDialogServiceSub
   */
  constructor(
    private getMessagesServiceSub: GetMessagesService,
    private modalServiceSub: NgbModal
  ) {
    super(
      modalServiceSub
    );
  }

  /**
   * 受け取ったメッセージIDをもとに、
   * messages.xmlからデータを検索し、アラートを表示。
   *
   * @param {string} mesId メッセージID 兼 アラートのタイトル
   * @param {Array<string>} [replaceStr] 置換文字列
   * @param {Function} [closeHander] 閉じる時に実行する処理
   * @memberof PlessDialogService
   */
  openAlertGetMessage(mesId: string, replaceStr?: Array<string>, closeHander?: Function) {
    let text = String('システムエラーが発生しました。\nシステム管理者までご連絡ください。');
    let title = String('S0107');
    let buttons: Array<PlessAlert> = [PlessAlert.Yes_Error];
    let defaultButton = Number(4);

    // messages.xmlのデータ取得サービス呼び出し
    this.getMessagesServiceSub.execute(mesId, (message: any) => {

      // messageがnull
      // もしくは、Buttonが3ではない場合は、システムエラーのまま設定
      if (message !== null) {
        text = message.Description[0];
        title = mesId;
        defaultButton = Number(message.DefaultButton[0]);

        if (Number(message.Button[0]) === 1) {
          // I0508などINFO系で「はい」ボタンだけ置きたい場合に使用する想定
          buttons = [PlessAlert.Yes];
        } else if (Number(message.Button[0]) === 3) {
          buttons = [PlessAlert.No, PlessAlert.Yes];
        } else if (Number(message.Button[0]) === 9) {
          // W0030やE0118など警告やエラー系で「はい」ボタンだけ置きたい場合に使用する想定
          // リプレイス開発時にボタン押下時のハンドラで
          // 「はい」を押された時に動いてほしくない処理が動いてしまう現象が多発したため
          // 仕方なく追加したエラー用「はい」ボタン
          buttons = [PlessAlert.Yes_Error];
        }

        // 置換文字列が送られて来た場合は、
        // 置換文字列を検索し、表示メッセージを編集する
        if (replaceStr !== null && replaceStr !== undefined) {
          Object.keys(replaceStr).forEach((key) => {
            text = text.replace('{' + key + '}', replaceStr[key]);
          });
        }
      }

      const ref = this.openModal(PlessAlertSubComponent);
      const instance = ref.componentInstance as PlessAlertSubComponent;
      instance.setData(text, title, buttons, defaultButton);
      instance.closeHander = closeHander;
      instance.closeFunction = () => {
        ref.close();
      };
    });
  }

  /**
   * 指定コンポーネントをモードレスで表示
   *
   * @param {*} parentComponent 親コンポーネント
   * @param {*} component 表示するコンポーネントクラス
   * @param {ViewContainerRef} viewContainerRef コンポーネントを追加するコンテナ
   * @param {ComponentRef<any>} componentRef
   * @param {*} [valueObject=null] 受け渡しを行う値
   * @param {number} [openType=0] 【既にポップアップが表示されていた場合】0:最前列に移動する 1:再度開く 2:閉じる
   * @param {string} [initLockFlg=''] 読み取り専用フラグ ※サブ画面起動時、親画面でロック取得を実行し、他ユーザにロックされていた場合、サブ画面をロックする為に使用する
   * @param {boolean} [appendBody=false] bodyに追加
   * @returns {*}
   * @memberof PlessDialogServiceSub
   */
  public openModelessSub(
    parentComponent: any,
    component: any,
    viewContainerRef: ViewContainerRef,
    componentRef: ComponentRef<any>,
    valueObject: any = null,
    openType = 0,
    initLockFlg = '',
    appendBody: boolean = false): any {

    // 最初にポップアップを閉じたかどうかを所持しているフラグ。
    // 現在は openType で2が指定されていた時のみ利用される。
    let closedFlag = Boolean(false);
    let retComponentRef: ComponentRef<any> = componentRef;

    // 既にポップアップが開かれていた場合、最初に該当画面をリムーブし、ポップアップが開かれていない状態にしている
    if (retComponentRef != null && parentComponent.isOpened(retComponentRef.instance.componentName.substr(0, 8))) {
      if (openType === 1 || openType === 2) {
        closedFlag = this.closePopUp(retComponentRef.instance);
      }
    }

    // openTypeが2でかつ閉じる処理を行っていた場合、そのまま処理を終了する
    if (openType === 2 && closedFlag) {
      return null;
    }

    if (retComponentRef != null && parentComponent.isOpened(retComponentRef.instance.componentName.substr(0, 8))) {
      // 既にポップアップが開かれていた場合、該当画面を最前列へ
      this.bringToFront(retComponentRef.instance);
      setTimeout(() => {
        retComponentRef.instance.setInputVO(valueObject);
      });
    } else {
      // モードレス表示
      retComponentRef = this.openModeless(component, viewContainerRef, appendBody);
      retComponentRef.instance.iInputVOArray = valueObject;
      setTimeout(() => {
        retComponentRef.instance.inputParameterSetting();
      });
    }
    retComponentRef.instance.initLock = initLockFlg;
    return retComponentRef;
  }

  /**
   * 最前面に表示する
   *
   * @private
   * @memberof PlessDialogServiceSub
   */
  private bringToFront(childComponent: any) {
    const zIndex: number = childComponent.elementRef.nativeElement.children[0].style.zIndex;
    const maxZIndex = this.getMaxZIndex();
    // 自身より前面に表示されているものを背面に回す
    this.sendToBack(zIndex);
    // 自身を最前面に表示する
    childComponent.elementRef.nativeElement.children[0].style.zIndex = maxZIndex.toString();
  }

  /**
   * 自身より前に表示されているものは背面に送る(zIndexを-1する)
   *
   * @private
   * @param {number} targetZIndex
   * @memberof PlessDialogServiceSub
   */
  private sendToBack(targetZIndex: number): void {
    const defaultView = document.defaultView;
    const elements = document.getElementsByClassName(this.CLASS_NAME);
    const length = elements.length;
    for (let i = Number(0); i < length; i++) {
      const element = elements[i];
      let zIndex = element['style'].zIndex;
      if (!zIndex) {
        const css = element['currentStyle'] || defaultView.getComputedStyle(element, null);
        zIndex = css ? css.zIndex : 0;
      }
      zIndex -= 0;
      if (zIndex > targetZIndex) {
        zIndex--;
        element['style'].zIndex = zIndex;
      }
    }
  }

  /**
   * 表示しているplessTitleWindowのzIndex最大値を取得する
   *
   * @private
   * @returns {number}
   * @memberof PlessDialogServiceSub
   */
  private getMaxZIndex(): number {
    let maxZIndex = PlessDialogService.MIN_Z_INDEX;
    const defaultView = document.defaultView;
    const elements = document.getElementsByClassName(this.CLASS_NAME);
    const length = elements.length;
    for (let i = Number(0); i < length; i++) {
      const element = elements[i];
      let zIndex = element['style'].zIndex;
      if (!zIndex) {
        const css = element['currentStyle'] || defaultView.getComputedStyle(element, null);
        zIndex = css ? css.zIndex : 0;
      }
      zIndex -= 0;
      if (maxZIndex < zIndex) {
        maxZIndex = zIndex;
      }
    }
    return maxZIndex;
  }

  /**
   * ポップアップ画面を閉じる。<br/>
   * PopUpManager.removePopUp()との違いは、渡された型と一致したポップアップウィンドウを削除する点。<br/>
   * 配列からの削除処理はdeletePopUpメソッドに依頼しているので、詳しくはそちらを参照。<br/>
   *
   * @param className 削除したいポップアップウィンドウの型
   *
   * @return 削除を行ったかどうかのフラグ。true:削除、false:未削除
   */
  public closePopUp(component: any): boolean {
    if (component.closeFunction != null) {
      component.closeFunction();
      return true;
    } else {
      return false;
    }
  }

  /**
   * アラートを表示
   *
   * @param text 表示する文字列(改行は\nで指定)
   * @param title アラートのタイトル
   * @param buttons 表示するボタン
   * @param closeHander 閉じる時に実行する処理
   */
  openAlert(text: string, title?: string, buttons?: Array<PlessAlert>, closeHander?: Function) {
    const ref = this.openModal(PlessAlertSubComponent);
    const instance = ref.componentInstance as PlessAlertSubComponent;
    instance.setData(text, title, buttons);
    instance.closeHander = closeHander;
    instance.closeFunction = () => {
      ref.close();
    };
  }

  /**
   * plessTitleWindowを表示しているか判定
   *
   */
  public isOpened():boolean {
    return this.getMaxZIndex() > PlessDialogService.MIN_Z_INDEX;
  }
}
